import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { get_api as getApi } from '../../../redux/api_funcs';
import { get_api_url as getApiUrl } from '../../../utils/urls';
import PeriodFilter from './PeriodFilter';
import StatusMultiSelect from './StatusMultiSelect';
import { readPeriodParams, resolvePeriod } from './period_utils';
import { downloadCsv } from './csv_utils';
import './trial_points.css';

const PAGE_SIZE = 50;

const CSV_HEADERS = [
  'Teacher ID',
  'Teacher Name',
  'Email',
  'Profile Status',
  'Bookings',
  'Finished Sessions',
  'Starting',
  'Added',
  'Deducted',
  'Remaining',
  '1on1 Conversions',
  'GC Conversions'
];

const csvRow = (row) => [
  row.teacher_id,
  row.teacher_name,
  row.email,
  row.profile_status,
  row.bookings,
  row.finished_count,
  row.starting_balance,
  row.points_added,
  row.points_deducted,
  row.ending_balance,
  row.conversions_1on1,
  row.conversions_gc
];

// /trial_points — all-teachers points movement for the period in the URL params.
function TrialPointsList() {
  const history = useHistory();
  const location = useLocation();
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total_pages: 0, total_count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [exporting, setExporting] = useState(false);

  const urlParams = new URLSearchParams(location.search);
  const page = Math.max(1, parseInt(urlParams.get('page') || '1', 10) || 1);
  const search = urlParams.get('search') || '';
  const statuses = (urlParams.get('statuses') || '').split(',').filter(Boolean);
  const profileStatus = urlParams.get('profile') || '';
  const { range, customStart, customEnd } = readPeriodParams(location.search);
  const { startDate, endDate } = resolvePeriod(range, customStart, customEnd);

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  const buildQuery = (extra = {}) => {
    const qp = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (search) qp.append('search', search);
    if (statuses.length) qp.append('statuses', statuses.join(','));
    if (profileStatus) qp.append('profile_status', profileStatus);
    Object.entries(extra).forEach(([k, v]) => qp.append(k, v));
    return qp;
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    const qp = buildQuery({ page: String(page), page_size: String(PAGE_SIZE) });
    const url = getApiUrl(`/v2/cms_user/trial_points/summary?${qp.toString()}`, '', true);
    getApi(url, true)
      .then((res) => {
        if (cancelled) return;
        const data = res?.data?.data || {};
        setRows(data.rows || []);
        setPagination(data.pagination || { page: 1, total_pages: 0, total_count: 0 });
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load trial points. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, page, search, statuses.join(','), profileStatus]);

  const updateParams = (updates) => {
    const p = new URLSearchParams(location.search);
    Object.entries(updates).forEach(([k, v]) => (v ? p.set(k, v) : p.delete(k)));
    history.replace({ pathname: location.pathname, search: `?${p.toString()}` });
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    updateParams({ search: searchInput.trim(), page: '' });
  };

  const onDownloadCsv = () => {
    setExporting(true);
    const qp = buildQuery({ export: 'true' });
    const url = getApiUrl(`/v2/cms_user/trial_points/summary?${qp.toString()}`, '', true);
    getApi(url, true)
      .then((res) => {
        const allRows = res?.data?.data?.rows || [];
        downloadCsv(
          `trial-points-${startDate}-to-${endDate}.csv`,
          CSV_HEADERS,
          allRows.map(csvRow)
        );
      })
      .catch(() => setError('Failed to export CSV. Please try again.'))
      .finally(() => setExporting(false));
  };

  const openDetail = (teacherUuid) => {
    window.open(`/trial_points/${teacherUuid}${location.search}`, '_blank', 'noopener,noreferrer');
  };

  const renderRow = (row) => (
    <div className="tp-row" key={row.teacher_uuid}>
      <div className="tp-col tp-left tp-wide" style={{ fontWeight: 600 }}>
        <a
          href={`/teacherprofile?id=${encodeURIComponent(row.teacher_uuid)}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {row.teacher_name || row.teacher_uuid}
        </a>
        <div className="tp-subtext">{row.email}</div>
      </div>
      <div className="tp-col">
        <span
          className={`tp-badge tp-badge-${row.profile_status === 'hidden' ? 'hidden' : 'visible'}`}
        >
          {row.profile_status === 'hidden' ? 'Hidden' : 'Visible'}
        </span>
      </div>
      <div className="tp-col">{row.bookings}</div>
      <div className="tp-col">{row.finished_count}</div>
      <div className="tp-col">{row.starting_balance}</div>
      <div className="tp-col" style={{ color: 'var(--state-success)', fontWeight: 600 }}>
        {row.points_added}
      </div>
      <div className="tp-col" style={{ color: 'var(--state-error)', fontWeight: 600 }}>
        {row.points_deducted}
      </div>
      <div className="tp-col" style={{ color: 'var(--brand-primary)', fontWeight: 600 }}>
        {row.ending_balance}
      </div>
      <div className="tp-col">{row.conversions_1on1}</div>
      <div className="tp-col">{row.conversions_gc}</div>
      <div className="tp-col">
        <button type="button" className="link-btn" onClick={() => openDetail(row.teacher_uuid)}>
          Details
        </button>
      </div>
    </div>
  );

  return (
    <div className="common-page-container">
      <div className="common-page-header">
        <div className="common-page-title">Trial Points</div>
      </div>
      <div>
        <PeriodFilter />
        <div className="tp-search">
          <StatusMultiSelect
            selected={statuses}
            onChange={(next) => updateParams({ statuses: next.join(','), page: '' })}
          />
          <select
            value={profileStatus}
            onChange={(e) => updateParams({ profile: e.target.value, page: '' })}
            className="tp-multiselect-toggle"
          >
            <option value="">Profile: All</option>
            <option value="visible">Profile: Visible</option>
            <option value="hidden">Profile: Hidden</option>
          </select>
          <form onSubmit={onSearchSubmit} className="tp-search" style={{ margin: 0 }}>
            <input
              type="text"
              value={searchInput}
              placeholder="Teacher name"
              onChange={(e) => setSearchInput(e.target.value)}
              className="tp-search-input"
            />
            <button type="submit" className="link-btn" style={{ margin: 0 }}>
              Search
            </button>
          </form>
          <button
            type="button"
            className="link-btn"
            style={{ margin: 0 }}
            disabled={exporting}
            onClick={onDownloadCsv}
          >
            {exporting ? 'Exporting…' : 'Download CSV'}
          </button>
        </div>
      </div>
      {error ? <div style={{ color: 'var(--state-error)' }}>{error}</div> : null}
      <div className="tp-table">
        <div className="tp-row tp-head">
          <div className="tp-col tp-left tp-wide">Teacher</div>
          <div className="tp-col">Profile</div>
          <div className="tp-col">Bookings</div>
          <div className="tp-col">Finished</div>
          <div className="tp-col">Starting</div>
          <div className="tp-col">Added</div>
          <div className="tp-col">Deducted</div>
          <div className="tp-col">Remaining</div>
          <div className="tp-col">1on1 Conv</div>
          <div className="tp-col">GC Conv</div>
          <div className="tp-col">Actions</div>
        </div>
        {loading ? (
          <div className="tp-row">
            <div className="tp-col tp-left">Loading…</div>
          </div>
        ) : null}
        {!loading && rows.length === 0 ? (
          <div className="tp-row">
            <div className="tp-col tp-left">
              No teachers with trial-points activity in this period
            </div>
          </div>
        ) : null}
        {!loading && rows.map(renderRow)}
      </div>
      {pagination.total_pages > 1 ? (
        <div className="tp-pagination">
          <button
            type="button"
            className="ct-small-btn"
            disabled={page <= 1}
            onClick={() => updateParams({ page: String(page - 1) })}
          >
            Prev
          </button>
          <span>
            Page {pagination.page} of {pagination.total_pages} ({pagination.total_count} teachers)
          </span>
          <button
            type="button"
            className="ct-small-btn"
            disabled={page >= pagination.total_pages}
            onClick={() => updateParams({ page: String(page + 1) })}
          >
            Next
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default TrialPointsList;
